import { Handle, Position, type NodeProps, type Node } from '@xyflow/react'
import type { C4NodeData } from './elkLayout'
import { categoryIcon, categoryTint } from './categoryStyle'

const HEALTH_COLOR: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-neutral-400',
}

/**
 * Custom C4 node: a category icon + a human display name, the precise id as a
 * monospace subtitle, a subtle category tint, and an accessible health dot.
 */
export function C4Node({ data }: NodeProps<Node<C4NodeData>>) {
  const color = HEALTH_COLOR[data.health] ?? HEALTH_COLOR.unknown
  const Icon = categoryIcon(data.category)
  // Keep the precise id visible; fall back to tech/kind when it equals the label.
  const subtitle = data.rawId && data.rawId !== data.label ? data.rawId : (data.technology ?? data.kind)
  return (
    <div className={`w-[200px] rounded border border-neutral-300 px-3 py-2 shadow-sm dark:border-neutral-700 ${categoryTint(data.category)}`}>
      <Handle type="target" position={Position.Top} />
      <div className="flex items-center gap-2">
        <Icon size={15} className="shrink-0 text-neutral-500 dark:text-neutral-400" aria-label={data.category} />
        <span className="truncate text-sm font-medium" title={data.label}>{data.label}</span>
        <span className={`ml-auto h-2.5 w-2.5 shrink-0 rounded-full ${color}`} title={data.health} aria-label={data.health} />
      </div>
      <div className="mt-0.5 flex items-center justify-between gap-2 pl-[23px] text-xs text-neutral-500">
        <span className="truncate font-mono" title={subtitle}>{subtitle}</span>
        {data.hasChildren && <span aria-hidden>⤵</span>}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
